import { getApiUrl, STORAGE_KEYS } from './config';
import type { FileUploadResponse } from './types';

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Upload a file to the server
 * @param file - The file to upload
 * @param onProgress - Optional callback for upload progress
 * @returns Upload response with file metadata
 */
export const uploadFile = async (
  file: File,
  onProgress?: (progress: UploadProgress) => void,
  dataSourceId?: string,
  signal?: AbortSignal
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  if (dataSourceId) {
    formData.append('dataSourceId', dataSourceId);
  }

  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Track upload progress
    if (onProgress) {
      xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
        if (e.lengthComputable) {
          const progress: UploadProgress = {
            loaded: e.loaded,
            total: e.total,
            percentage: Math.round((e.loaded * 100) / e.total),
          };
          onProgress(progress);
        }
      });
    }

    // Handle completion
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response: FileUploadResponse = JSON.parse(xhr.responseText);
          resolve(response);
        } catch {
          reject(new Error('Invalid response format'));
        }
      } else if (xhr.status === 413) {
        reject(new Error('File size exceeds maximum limit (500MB)'));
      } else {
        try {
          const errorResponse = JSON.parse(xhr.responseText);
          reject(new Error(errorResponse.message || 'Failed to upload file'));
        } catch {
          reject(new Error('Failed to upload file. Please try again.'));
        }
      }
    });

    // Handle errors
    xhr.addEventListener('error', () => {
      reject(new Error('Network error occurred during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload cancelled'));
    });

    // Open and send request
    // Wire AbortSignal to XHR
    if (signal) {
      if (signal.aborted) {
        reject(new Error('Upload cancelled'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.open('POST', getApiUrl('/connections/upload'));

    if (accessToken) {
      xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }

    xhr.send(formData);
  });
};

export const uploadService = {
  uploadFile,
};